import moment from 'moment'
import rules from '@scoutgg/scoring-rules'
import {Base, model, belongsTo, endpoint, hasMany, getFrom, property} from '@scoutgg/ftw/source/lib/model'
import serialize from '@scoutgg/ftw/source/decorators/serialize';

const MINUTE = 60000
const RULES = Symbol.for('rules')

function maybeMoment(value) {
  return value == null ? value : moment(value)
}
@model('Match')
@endpoint('matches', {root: '/api'})
@belongsTo('season')
@belongsTo('user')
@belongsTo('delegate', 'User')
@belongsTo('lastUpdateBy', 'User')
@belongsTo('teams', { foreign: 'teamIds' })
@belongsTo('events')
@belongsTo('lockedEvents', 'Event')
@belongsTo('lineups')
@belongsTo('lockedLineups', 'Lineup')
@hasMany('participations')
@hasMany('matchSources')
@getFrom('season', [
  'gameType',
  'isDuel',
  'isRacing',
  'isCrossCountry',
])
@serialize([ 'startTime', 'endTime' ])
export class Match extends Base {
  @property(maybeMoment) startTime;
  @property(maybeMoment) endTime;

  get sport() {
    return this.season &&
      this.season.league &&
      this.season.league.sport
  }
  get homeTeam() {
    return this.teams && this.teams[0]
  }
  get awayTeam() {
    return this.teams && this.teams[1]
  }
  get descriptor() {
    const round = this.round
    const name = this.name || (this.teams || []).map(t => t.name).join(' vs ')
    return `GW${round}${this.bout ? ' ' + this.bout : ''}: ${name}`
  }

  get home() {
    return this.homeTeam ? this.homeTeam.name : '???'
  }
  get away() {
    return this.awayTeam ? this.awayTeam.name : '???'
  }
  get duration() {
    return Math.ceil(this.durationMs / MINUTE)
  }
  get durationMs() {
    if(this.status === 'live') {
      const diff = -moment(this.timerReference || this.startTime).diff()
      const periods = this.rules?.periods() || [100, 100]
      const min = periods
        .slice(0, this.period - 1)
        .reduce((init, val) => init + val, 0)
      const max = periods
        .slice(0, this.period)
        .reduce((init, val) => init + val, 0)
      return Math.min(min * MINUTE + diff, max * MINUTE)
    } else {
      return this.gameLength * MINUTE
    }
  }
  get gameLength() {
    return (
      (this.rules?.periods() || [])
        .slice(0, this.period)
        .reduce((init, val) => (init += val), 0)
    )
  }

  get liveCount() {
    if(this.duration > this.gameLength) {
      let timeOverFlow = moment(this.gameLength * MINUTE)
      return `${timeOverFlow.diff(0, 'minutes')}'${timeOverFlow.format('ss')}"`
    }
    var stamp = moment(this.durationMs)
    return `${stamp.diff(0, 'minutes')}'${stamp.format('ss')}"`
  }

  get cricketType() {
    if((this.matchType || 'normal') === 'normal') return 't20'
    return this.matchType
  }

  opponent(team) {
    return team === this.homeTeam ? 'away' : 'home'
  }
  opposing(team) {
    return team === this.homeTeam ? this.awayTeam : this.homeTeam
  }
  get batches() {
    if(!this.periods) return
    return Array(this.periods).fill(1).map((_, i) => i + 1)
  }
  get hasGoalkeeper() {
    return this.rulePositions.includes('goalkeeper')
  }
  get hasMinutes() {
    return this.isDuel && ![
      'mma',
      'cricket',
      'kabaddi',
      'american_football',
      'baseball',
      'tennis',
    ].includes(this.sport)
  }
  get canEditMinutes() {
    return ![
      'football',
      'rugby_union',
      'tennis',
    ].includes(this.sport)
  }

  get subPairs() {
    const scores = this.subScore || []
    const ret = Array(Math.floor(scores.length / 2))
    for(var i = 0; i < scores.length; i += 2) {
      ret[i / 2] = scores.slice(i, i + 2)
    }

    return ret
  }
  get iconUrl() {
    return `https://statcenter.scoutgg.net/s3/uploads/assets/${this.icon || 'team_logo_placeholder.svg'}`
  }

  get isTeamDuel() {
    return this.sport === 'alpine' && this.matchType === 'team'
  }

  get isEsports() {
    return ['csgo', 'lol', 'dota2'].includes(this.sport)
  }

  get isIndividualDuel() {
    return this.sport === 'alpine' && this.matchType === 'individual_duel'
  }

  get isTimeTrial() {
    if(!(this.season && this.season.isTimeTrial)) return false
    if(this.sport === 'cycling' && !['normal', 'team'].includes(this.matchType || 'normal')) {
      return false
    }
    return true
  }

  get rules() {
    if(this[RULES]) return this[RULES]
    const ruleSet = rules(this.sport, this.ruleVersion)
    if(!ruleSet) return
    ruleSet.matchType = this.matchType
    this[RULES] = ruleSet
    return ruleSet
  }

  get rulePositions() {
    return this.rules?.positions()
  }
}

Match.prototype._load = Match.prototype.load
Match.prototype.load = function(...args) {
  return this._load(...args).then(data => {
    if (args.includes('formations') || args.includes('action-points')) {
      return data
    }

    return Object.assign({
      events: data.match.events,
      lineups: data.match.lineups,
      locations: data.locations,
      overrides: data.overrides
    }, data)
  })
}
